import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, FlaskConical, Award, Globe } from "lucide-react";
import { MaskLine, Reveal } from "../components/Reveal";
import Marquee from "../components/Marquee";
import SEO, { SEO_CONFIG } from "../components/SEO";
import { useLanguage } from "../i18n/LanguageContext";

export default function About() {
  const { t, lang } = useLanguage();
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const y2 = useTransform(scrollYProgress, [0, 1], ["8%", "-8%"]);

  const TIMELINE = [
    { year: "2020", title: t("about.t2020Title"), body: t("about.t2020Body") },
    { year: "2021", title: t("about.t2021Title"), body: t("about.t2021Body") },
    { year: "2022", title: t("about.t2022Title"), body: t("about.t2022Body") },
    { year: "2025", title: t("about.t2025Title"), body: t("about.t2025Body") },
  ];

  const VALUES = [
    { icon: ShieldCheck, title: t("about.v1Title"), body: t("about.v1Body") },
    { icon: FlaskConical, title: t("about.v2Title"), body: t("about.v2Body") },
    { icon: Award, title: t("about.v3Title"), body: t("about.v3Body") },
    { icon: Globe, title: t("about.v4Title"), body: t("about.v4Body") },
  ];

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": SEO_CONFIG.about.title,
    "description": SEO_CONFIG.about.description,
    "url": "https://www.ashlarbuildchem.com/about",
    "mainEntity": {
      "@type": "Organization",
      "name": "Ashlar Buildchem",
      "url": "https://www.ashlarbuildchem.com",
      "logo": "https://www.ashlarbuildchem.com/logo.png"
    }
  };

  return (
    <div className="pt-20" data-testid="about-page">
      <SEO {...SEO_CONFIG.about} schema={aboutSchema} />
      {/* Header */}
      <section className="mx-auto max-w-[1600px] px-6 pb-20 pt-20 md:px-10 md:pt-28">
        <span className="label text-primary">{t("about.eyebrow")}</span>
        <h1
          className={`mt-6 max-w-5xl font-display text-3xl font-bold sm:text-5xl md:text-[min(7.5vw,6.5rem)] ${
            lang === "hi"
              ? "normal-case leading-[1.15] tracking-normal"
              : "uppercase leading-[0.9] tracking-tighter"
          }`}
        >
          <MaskLine delay={0.05}>{t("about.h1a")}</MaskLine>
          <MaskLine delay={0.18}>{t("about.h1b")}</MaskLine>
          <MaskLine delay={0.3}><span className="text-primary">{t("about.h1c")}</span></MaskLine>
        </h1>
        <Reveal delay={0.4} className="mt-10 max-w-2xl">
          <p className="text-lg leading-relaxed text-muted-foreground">
            {t("about.intro")}
          </p>
        </Reveal>
      </section>

      {/* Overlapping images */}
      <section ref={imgRef} className="relative mx-auto max-w-[1600px] px-6 pb-28 md:px-10">
        <div className="grid grid-cols-2 gap-6 md:gap-10">
          <motion.div style={{ y: y1 }} className="clip-corner overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1581092160562-40aa08e78837?q=80&w=1400&auto=format&fit=crop"
              alt={t("about.labAlt")}
              className="h-[280px] w-full object-cover grayscale md:h-[520px]"
            />
          </motion.div>
          <motion.div style={{ y: y2 }} className="mt-16 overflow-hidden md:mt-32">
            <img
              src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?q=80&w=1400&auto=format&fit=crop"
              alt={t("about.siteAlt")}
              className="h-[280px] w-full object-cover grayscale md:h-[520px]"
            />
          </motion.div>
        </div>
      </section>

      {/* Marquee */}
      <section className="border-y border-black/10 py-8">
        <Marquee items={[t("about.v1Title"), t("about.t2021Title"), "ISO 9001:2015", "ISI Marked"]} duration={42} />
      </section>

      {/* Values */}
      <section className="mx-auto max-w-[1600px] px-6 py-28 md:px-10 md:py-40" data-testid="about-values">
        <Reveal>
          <span className="label text-primary">{t("about.standFor")}</span>
          <h2 className="mt-6 max-w-3xl font-display text-4xl font-semibold leading-[0.95] tracking-tight md:text-7xl">
            {t("about.principlesTitle")}
          </h2>
        </Reveal>
        <div className="mt-16 grid gap-px bg-black/10 md:grid-cols-4">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08} className="bg-background">
              <div className="group h-full p-8 transition-colors duration-300 hover:bg-secondary md:p-10">
                <v.icon className="h-9 w-9 text-primary" strokeWidth={1.3} />
                <h3 className="mt-8 font-display text-xl font-semibold">{v.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{v.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Timeline */}
      <section className="border-t border-black/10 bg-secondary/30" data-testid="about-timeline">
        <div className="mx-auto max-w-[1600px] px-6 py-28 md:px-10 md:py-40">
          <Reveal>
            <span className="label text-primary">{t("about.journey")}</span>
            <h2 className="mt-6 font-display text-4xl font-semibold leading-[0.95] tracking-tight md:text-7xl">
              {t("about.journeyTitle")}
            </h2>
          </Reveal>
          <div className="mt-16">
            {TIMELINE.map((item, i) => (
              <Reveal key={item.year} delay={i * 0.06}>
                <div className="group grid grid-cols-1 items-start gap-4 border-t border-black/10 py-10 md:grid-cols-[200px_1fr] md:gap-12">
                  <span className="font-mono text-4xl font-bold text-primary md:text-5xl">{item.year}</span>
                  <div>
                    <h3 className="font-display text-2xl font-semibold md:text-3xl">{item.title}</h3>
                    <p className="mt-3 max-w-2xl text-base text-muted-foreground">{item.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-black/10 bg-primary text-primary-foreground">
        <div className="mx-auto flex max-w-[1600px] flex-col items-start justify-between gap-8 px-6 py-24 md:flex-row md:items-center md:px-10">
          <h2 className="font-display text-4xl font-bold leading-[0.95] tracking-tight md:text-6xl">
            {t("about.partner")}
          </h2>
          <Link to="/contact" data-testid="about-cta" className="group inline-flex items-center gap-3 bg-background px-10 py-6 text-foreground transition-transform duration-200 hover:-translate-y-1">
            <span className="label">{t("about.getInTouch")}</span>
            <ArrowUpRight className="h-5 w-5 text-primary transition-transform duration-200 group-hover:rotate-45" />
          </Link>
        </div>
      </section>
    </div>
  );
}
